import React from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';

import { PAYMENT_FACILITIES } from '@medipass/utils/constants';
import sdk from '@medipass/web-sdk';

import { loadPayPalSDK } from './loadSDK';
import type { PayPalCheckoutContext } from './types';

export function getPayPalCheckoutContext(
  transaction: Record<string, any>,
  session: Record<string, any>,
  paymentFacility: string,
): PayPalCheckoutContext | undefined {
  if (
    paymentFacility !== PAYMENT_FACILITIES.MEDIPASS_PAYMENTS ||
    transaction._schemaVersion !== '3' ||
    session.futureId ||
    transaction.futureType ||
    !transaction._id ||
    !transaction.businessId ||
    !session._id ||
    !Number.isSafeInteger(transaction.amountBalance) ||
    transaction.amountBalance <= 0
  )
    return undefined;
  return {
    businessId: transaction.businessId,
    invoiceId: transaction._id,
    sessionId: session._id,
    // amountBalance already includes shipping; adding shipping again would overcharge.
    amount: (transaction.amountBalance / 100).toFixed(2),
    currencyCode: transaction.currencyCode || 'AUD',
    expiresAt: session.isExpiryDisplayed ? transaction.paymentRequestUrlExpiry : undefined,
  };
}

type OptionsContext = Pick<PayPalCheckoutContext, 'businessId' | 'sessionId'>;

export const PAYPAL_OPTIONS_TIMEOUT_MS = 5000;

async function getSalesOptions(businessId: string) {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      sdk.sales.getOptions(businessId, { requestConfig: { tokenType: 'session' } }),
      new Promise<never>((_resolve, reject) => {
        timeout = setTimeout(() => reject(new Error('PayPal options request timed out.')), PAYPAL_OPTIONS_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timeout);
  }
}

const paypalOptionsQuery = (context?: OptionsContext) => ({
  queryKey: ['paypal-options', context?.sessionId, context?.businessId],
  queryFn: () => getSalesOptions(context!.businessId),
  staleTime: 5 * 60 * 1000,
  retry: false as const,
  retryOnMount: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  networkMode: 'always' as const,
  refetchOnWindowFocus: false,
});

// Start this alongside checkout resources, then await it before showing checkout.
// Only the options response blocks the page; SDK preloading stays in the background.
export async function prefetchPayPalOptions(client: QueryClient, context: OptionsContext & { currencyCode?: string }) {
  if (!context.businessId || !context.sessionId) return;
  const query = paypalOptionsQuery(context);
  // Preserve a failed initial check so mounting checkout cannot retry and make PayPal pop in later.
  if (client.getQueryState(query.queryKey)?.status === 'error') return;
  try {
    const result = await client.fetchQuery(query);
    const options = result.paymentTypes?.paypal;
    if (context.currencyCode && options?.clientId && options.accountId) {
      void loadPayPalSDK({ ...options, currencyCode: context.currencyCode }).catch(() => undefined);
    }
  } catch {
    // PayPal availability must never prevent payment with another method.
  }
}

export function usePayPalOptions(context?: PayPalCheckoutContext) {
  const query = useQuery({ ...paypalOptionsQuery(context), enabled: Boolean(context) });
  const result = query.isError ? undefined : query.data?.paymentTypes?.paypal;
  const options = context && result?.clientId && result.accountId ? result : undefined;
  const currencyCode = context?.currencyCode;
  React.useEffect(() => {
    if (options && currencyCode) {
      // A failed preload is retried by the interactive controls when selected.
      void loadPayPalSDK({ ...options, currencyCode }).catch(() => undefined);
    }
  }, [options, currencyCode]);
  return { options, isChecking: Boolean(context) && query.isPending && query.fetchStatus === 'fetching' };
}
